<script setup>
import { ref, computed } from 'vue';
import { Card } from 'primevue';
import InputNumber from 'primevue/inputnumber';
import Dropdown from 'primevue/dropdown';
import ANButton from '@/components/common/AnButton.vue';
import Chart from 'primevue/chart';
import { calculateRentVsBuy } from '@/utils/financeCalculators.js';

// Form data
const formData = ref({
  city: 'Mumbai',
  monthlyRent: 50000,
  annualRentIncreasePercent: 5,
  homePrice: 15000000,
  downPaymentPercent: 20,
  loanInterestRate: 8.5,
  loanTenureYears: 20,
  investmentReturnRate: 12,
  startYear: 2025
});

const result = ref(null);
const chartData = ref({});
const chartOptions = ref({});

// City options
const cityOptions = [
  { label: 'Mumbai', value: 'Mumbai' },
  { label: 'Delhi NCR', value: 'Delhi NCR' },
  { label: 'Bangalore', value: 'Bangalore' },
  { label: 'Hyderabad', value: 'Hyderabad' },
  { label: 'Pune', value: 'Pune' },
  { label: 'Chennai', value: 'Chennai' },
  { label: 'Kolkata', value: 'Kolkata' },
  { label: 'Ahmedabad', value: 'Ahmedabad' }
];

// Computed properties
const isValidInput = computed(() => {
  return formData.value.city &&
         formData.value.monthlyRent > 0 &&
         formData.value.homePrice > 0 &&
         formData.value.downPaymentPercent > 0 &&
         formData.value.loanTenureYears > 0 &&
         formData.value.loanInterestRate > 0 &&
         formData.value.investmentReturnRate > 0;
});

const loanAmount = computed(() => {
  return formData.value.homePrice * (1 - formData.value.downPaymentPercent / 100);
});

const downPaymentAmount = computed(() => {
  return formData.value.homePrice * (formData.value.downPaymentPercent / 100);
});

// Calculate Rent vs Buy
const calculate = () => {
  if (!isValidInput.value) return;

  result.value = calculateRentVsBuy(formData.value);
  generateChart();
  // Scroll to results section after calculation
  setTimeout(() => {
    const resultsElement = document.querySelector('.result-card');
    if (resultsElement) {
      resultsElement.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start',
        inline: 'nearest' 
      });
    }
  }, 100);
};

// Generate chart data
const generateChart = () => {
  if (!result.value || !result.value.yearlyData) return;

  const years = result.value.yearlyData.map(d => `Year ${d.year}`);
  const investmentValues = result.value.yearlyData.map(d => d.investmentValue);
  const homeValues = result.value.yearlyData.map(d => d.homeValue);
  const rentPaid = result.value.yearlyData.map(d => d.totalRentPaid);

  chartData.value = {
    labels: years,
    datasets: [
      {
        label: 'Investment Value (Renting)',
        data: investmentValues,
        backgroundColor: 'rgba(54, 162, 235, 0.6)',
        borderColor: 'rgba(54, 162, 235, 1)',
        borderWidth: 2,
        fill: false
      },
      {
        label: 'Home Value',
        data: homeValues,
        backgroundColor: 'rgba(255, 99, 132, 0.6)',
        borderColor: 'rgba(255, 99, 132, 1)',
        borderWidth: 2,
        fill: false
      },
      {
        label: 'Cumulative Rent Paid',
        data: rentPaid,
        backgroundColor: 'rgba(255, 206, 86, 0.6)',
        borderColor: 'rgba(255, 206, 86, 1)',
        borderWidth: 2,
        fill: false
      }
    ]
  };

  chartOptions.value = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: `Rent vs Buy Analysis - ${formData.value.city}`
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function(value) {
            return '₹' + (value / 10000000).toFixed(1) + 'Cr';
          }
        }
      }
    }
  };
};

// Get city-specific insights
const getCityInsights = () => {
  const insights = {
    'Mumbai': {
      avgAppreciation: '6.2%',
      marketTrend: 'Stable growth expected',
      rentalYield: '2.8-3.2%',
      recommendation: 'High property prices favor renting for flexibility'
    },
    'Delhi NCR': {
      avgAppreciation: '5.1%',
      marketTrend: 'Recovery phase with moderate growth',
      rentalYield: '3.0-3.5%',
      recommendation: 'Emerging opportunities in peripheral areas'
    },
    'Bangalore': {
      avgAppreciation: '7.8%',
      marketTrend: 'Strong IT sector driving demand',
      rentalYield: '3.5-4.0%',
      recommendation: 'Good buying opportunities in growing corridors'
    },
    'Hyderabad': {
      avgAppreciation: '6.1%',
      marketTrend: 'Consistent growth with IT expansion',
      rentalYield: '4.0-4.5%',
      recommendation: 'Balanced market suitable for both renting and buying'
    },
    'Pune': {
      avgAppreciation: '7.3%',
      marketTrend: 'Steady appreciation driven by IT sector',
      rentalYield: '3.8-4.2%',
      recommendation: 'Growing market with good investment potential'
    },
    'Chennai': {
      avgAppreciation: '7.1%',
      marketTrend: 'Moderate growth with industrial development',
      rentalYield: '4.2-4.8%',
      recommendation: 'Affordable property prices favor buying for long-term'
    },
    'Kolkata': {
      avgAppreciation: '6.5%',
      marketTrend: 'Gradual recovery with infrastructure development',
      rentalYield: '5.0-5.5%',
      recommendation: 'Lower property prices make buying attractive'
    },
    'Ahmedabad': {
      avgAppreciation: '6.8%',
      marketTrend: 'Steady growth with business-friendly policies',
      rentalYield: '4.5-5.0%',
      recommendation: 'Balanced market with good rental and capital returns'
    }
  };
  
  return insights[formData.value.city] || insights['Mumbai'];
};

// Initialize
calculate();
</script>

<template>
  <div class="rent-vs-buy-calculator">
    <div class="calculator-grid">
      <!-- Input Form -->
      <Card class="input-card">
        <template #title>
          <h3>Rent vs Buy Calculator Input</h3>
        </template>
        <template #content>
          <div class="form-fields">
            <!-- City Selection -->
            <div class="field">
              <label for="city">City</label>
              <Dropdown
                id="city"
                v-model="formData.city"
                :options="cityOptions"
                optionLabel="label"
                optionValue="value"
                placeholder="Select City"
                class="input-field"
              />
              <small class="help-text">City-specific appreciation rates applied</small>
            </div>

            <!-- Rent Details -->
            <div class="section-title">Rental Information</div>
            
            <div class="field">
              <label for="rent">Monthly Rent (₹)</label>
              <InputNumber
                id="rent"
                v-model="formData.monthlyRent"
                :min="5000"
                :max="1000000"
                :step="5000"
                showButtons
                buttonLayout="horizontal"
                decrementButtonClass="p-button-secondary"
                incrementButtonClass="p-button-secondary"
                class="input-field"
              />
            </div>

            <div class="field">
              <label for="rentIncrease">Annual Rent Increase (%)</label>
              <InputNumber
                id="rentIncrease"
                v-model="formData.annualRentIncreasePercent"
                :min="0"
                :max="20"
                :step="0.5"
                :minFractionDigits="1"
                showButtons
                buttonLayout="horizontal"
                decrementButtonClass="p-button-secondary"
                incrementButtonClass="p-button-secondary"
                class="input-field"
              />
            </div>

            <!-- Property Details -->
            <div class="section-title">Property Information</div>
            
            <div class="field">
              <label for="homePrice">Property Price (₹)</label>
              <InputNumber
                id="homePrice"
                v-model="formData.homePrice"
                :min="1000000"
                :max="1000000000"
                :step="1000000"
                showButtons
                buttonLayout="horizontal"
                decrementButtonClass="p-button-secondary"
                incrementButtonClass="p-button-secondary"
                class="input-field"
              />
            </div>

            <div class="field">
              <label for="downPayment">Down Payment (%)</label>
              <InputNumber
                id="downPayment"
                v-model="formData.downPaymentPercent"
                :min="10"
                :max="100"
                :step="5"
                showButtons
                buttonLayout="horizontal"
                decrementButtonClass="p-button-secondary"
                incrementButtonClass="p-button-secondary"
                class="input-field"
              />
            </div>

            <!-- Loan Details -->
            <div class="section-title">Loan Information</div>
            
            <div class="field">
              <label for="loanRate">Loan Interest Rate (%)</label>
              <InputNumber
                id="loanRate"
                v-model="formData.loanInterestRate"
                :min="5"
                :max="20"
                :step="0.25"
                :minFractionDigits="2"
                showButtons
                buttonLayout="horizontal"
                decrementButtonClass="p-button-secondary"
                incrementButtonClass="p-button-secondary"
                class="input-field"
              />
            </div>

            <div class="field">
              <label for="tenure">Loan Tenure (Years)</label>
              <InputNumber
                id="tenure"
                v-model="formData.loanTenureYears"
                :min="5"
                :max="30"
                :step="1"
                showButtons
                buttonLayout="horizontal"
                decrementButtonClass="p-button-secondary"
                incrementButtonClass="p-button-secondary"
                class="input-field"
              />
            </div>

            <div class="field">
              <label for="investmentReturn">Investment Return Rate (%)</label>
              <InputNumber
                id="investmentReturn"
                v-model="formData.investmentReturnRate"
                :min="5"
                :max="25"
                :step="0.5"
                :minFractionDigits="1"
                showButtons
                buttonLayout="horizontal"
                decrementButtonClass="p-button-secondary"
                incrementButtonClass="p-button-secondary"
                class="input-field"
              />
              <small class="help-text">Expected return from alternative investments</small>
            </div>

            <!-- Calculate Button -->
            <ANButton
              label="Calculate"
              severity="primary"
              size="large"
              @click="calculate"
              :disabled="!isValidInput"
              class="calculate-btn"
            />
          </div>
        </template>
      </Card>

      <!-- Results -->
      <Card v-if="result" class="result-card">
        <template #title>
          <h3>Rent vs Buy Analysis</h3>
        </template>
        <template #content>
          <div class="results">
            <!-- Recommendation -->
            <div class="recommendation-card">
              <div class="recommendation-icon">
                {{ result.recommendation.includes('Renting') ? '🏠' : '🏡' }}
              </div>
              <div class="recommendation-text">
                <h4>{{ result.recommendation }}</h4>
                <p>Based on {{ formData.loanTenureYears }}-year analysis in {{ formData.city }}</p>
              </div>
            </div>

            <!-- Key Metrics -->
            <div class="metrics-grid">
              <div class="metric-item">
                <div class="metric-label">Monthly EMI</div>
                <div class="metric-value">₹{{ result.emi.toLocaleString('en-IN') }}</div>
              </div>
              <div class="metric-item">
                <div class="metric-label">Down Payment</div>
                <div class="metric-value">₹{{ result.downPayment.toLocaleString('en-IN') }}</div>
              </div>
              <div class="metric-item">
                <div class="metric-label">Final Home Value</div>
                <div class="metric-value">₹{{ result.finalHomeValue.toLocaleString('en-IN') }}</div>
              </div>
              <div class="metric-item">
                <div class="metric-label">Investment Value</div>
                <div class="metric-value">₹{{ result.totalInvestedValue.toLocaleString('en-IN') }}</div>
              </div>
            </div>

            <!-- Comparison Summary -->
            <div class="comparison-summary">
              <h4>Final Wealth Comparison</h4>
              <div class="comparison-bars">
                <div class="bar-item">
                  <div class="bar-label">Buying</div>
                  <div class="bar-container">
                    <div 
                      class="bar buy-bar" 
                      :style="{width: (result.finalHomeValue / Math.max(result.finalHomeValue, result.totalInvestedValue)) * 100 + '%'}"
                    ></div>
                  </div>
                  <div class="bar-value">₹{{ (result.finalHomeValue / 10000000).toFixed(2) }}Cr</div>
                </div>
                <div class="bar-item">
                  <div class="bar-label">Renting + Investing</div>
                  <div class="bar-container">
                    <div 
                      class="bar rent-bar" 
                      :style="{width: (result.totalInvestedValue / Math.max(result.finalHomeValue, result.totalInvestedValue)) * 100 + '%'}"
                    ></div>
                  </div>
                  <div class="bar-value">₹{{ (result.totalInvestedValue / 10000000).toFixed(2) }}Cr</div>
                </div>
              </div>
            </div>
          </div>
        </template>
      </Card>
    </div>

    <!-- City Insights -->
    <Card v-if="result" class="insights-card">
      <template #title>
        <h3>{{ formData.city }} Real Estate Insights</h3>
      </template>
      <template #content>
        <div class="insights-grid">
          <div class="insight-item">
            <div class="insight-label">Average Appreciation</div>
            <div class="insight-value">{{ getCityInsights().avgAppreciation }}</div>
          </div>
          <div class="insight-item">
            <div class="insight-label">Market Trend</div>
            <div class="insight-value">{{ getCityInsights().marketTrend }}</div>
          </div>
          <div class="insight-item">
            <div class="insight-label">Rental Yield</div>
            <div class="insight-value">{{ getCityInsights().rentalYield }}</div>
          </div>
          <div class="insight-item full-width">
            <div class="insight-label">Market Recommendation</div>
            <div class="insight-desc">{{ getCityInsights().recommendation }}</div>
          </div>
        </div>
      </template>
    </Card>

    <!-- Chart -->
    <Card v-if="result && chartData.labels" class="chart-card">
      <template #title>
        <h3>Rent vs Buy Comparison Over Time</h3>
      </template>
      <template #content>
        <div class="chart-container">
          <Chart type="line" :data="chartData" :options="chartOptions" class="chart" />
        </div>
      </template>
    </Card>

    <!-- Detailed Analysis Table -->
    <Card v-if="result && result.yearlyData" class="analysis-card">
      <template #title>
        <h3>Year-by-Year Analysis</h3>
      </template>
      <template #content>
        <div class="analysis-table">
          <div class="table-header">
            <span>Year</span>
            <span>Investment Value</span>
            <span>Home Value</span>
            <span>Cumulative Rent</span>
            <span>Difference</span>
          </div>
          <div 
            v-for="data in result.yearlyData" 
            :key="data.year"
            class="table-row"
          >
            <span>{{ data.year }}</span>
            <span>₹{{ (data.investmentValue / 100000).toFixed(1) }}L</span>
            <span>₹{{ (data.homeValue / 100000).toFixed(1) }}L</span>
            <span>₹{{ (data.totalRentPaid / 100000).toFixed(1) }}L</span>
            <span :class="data.investmentValue > data.homeValue ? 'positive' : 'negative'">
              {{ data.investmentValue > data.homeValue ? '+' : '' }}₹{{ ((data.investmentValue - data.homeValue) / 100000).toFixed(1) }}L
            </span>
          </div>
        </div>
      </template>
    </Card>
  </div>
</template>

<style scoped>
.rent-vs-buy-calculator {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.calculator-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
}

.form-fields {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.section-title {
  font-weight: 700;
  color: var(--an-primary);
  font-size: 16px;
  margin-top: 16px;
  padding-bottom: 8px;
  border-bottom: 2px solid var(--an-primary);
}

.field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.field label {
  font-weight: 600;
  color: var(--an-text-primary);
  font-size: 14px;
}

.help-text {
  color: var(--an-text-secondary);
  font-size: 12px;
  font-style: italic;
}

.input-field {
  width: 100%;
}

.calculate-btn {
  width: 100%;
  margin-top: 16px;
}

.results {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.recommendation-card {
  display: flex;
  gap: 16px;
  padding: 20px;
  background: linear-gradient(135deg, var(--an-primary), var(--an-secondary));
  color: var(--an-white);
  border-radius: 12px;
  align-items: center;
}

.recommendation-icon {
  font-size: 3rem;
}

.recommendation-text h4 {
  margin: 0 0 8px 0;
  font-size: 1.2rem;
}

.recommendation-text p {
  margin: 0;
  opacity: 0.9;
  font-size: 14px;
}

.metrics-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

.metric-item {
  text-align: center;
  padding: 16px;
  background: var(--an-background-light);
  border-radius: 8px;
  border: 1px solid var(--an-gray3);
}

.metric-label {
  font-size: 12px;
  color: var(--an-text-secondary);
  margin-bottom: 4px;
}

.metric-value {
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--an-text-primary);
}

.comparison-summary h4 {
  margin: 0 0 16px 0;
  color: var(--an-text-primary);
}

.comparison-bars {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.bar-item {
  display: flex;
  align-items: center;
  gap: 12px;
}

.bar-label {
  width: 120px;
  font-weight: 500;
  font-size: 14px;
}

.bar-container {
  flex: 1;
  height: 24px;
  background: var(--an-gray3);
  border-radius: 12px;
  overflow: hidden;
}

.bar {
  height: 100%;
  border-radius: 12px;
  transition: width 0.5s ease;
}

.buy-bar {
  background: linear-gradient(90deg, #ff6b6b, #ee5a24);
}

.rent-bar {
  background: linear-gradient(90deg, #4834d4, #6c5ce7);
}

.bar-value {
  width: 80px;
  text-align: right;
  font-weight: 600;
  font-size: 14px;
}

.insights-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
}

.insight-item {
  text-align: center;
  padding: 16px;
  background: var(--an-background-light);
  border-radius: 8px;
}

.insight-item.full-width {
  grid-column: 1 / -1;
  text-align: left;
}

.insight-label {
  font-size: 12px;
  color: var(--an-text-secondary);
  margin-bottom: 8px;
  font-weight: 500;
}

.insight-value {
  font-size: 1rem;
  font-weight: 700;
  color: var(--an-primary);
}

.insight-desc {
  font-size: 14px;
  color: var(--an-text-primary);
  line-height: 1.5;
}

.chart-container {
  height: 400px;
  width: 100%;
}

.chart {
  height: 100% !important;
  width: 100% !important;
}

.analysis-table {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 400px;
  overflow-y: auto;
}

.table-header,
.table-row {
  display: grid;
  grid-template-columns: 60px 140px 140px 140px 120px;
  gap: 16px;
  padding: 12px;
  align-items: center;
}

.table-header {
  font-weight: 700;
  background: var(--an-background-light);
  border-radius: 6px;
  color: var(--an-text-primary);
  position: sticky;
  top: 0;
}

.table-row {
  border-bottom: 1px solid var(--an-gray3);
  font-size: 14px;
}

.table-row:hover {
  background: var(--an-background-light);
}

.positive {
  color: var(--an-success);
  font-weight: 600;
}

.negative {
  color: var(--an-error);
  font-weight: 600;
}

/* Responsive Design */
@media (max-width: 768px) {
  .calculator-grid {
    grid-template-columns: 1fr;
  }
  
  .metrics-grid {
    grid-template-columns: 1fr;
  }
  
  .insights-grid {
    grid-template-columns: 1fr;
  }
  
  .recommendation-card {
    flex-direction: column;
    text-align: center;
  }
  
  .bar-item {
    flex-direction: column;
    gap: 8px;
  }
  
  .bar-label,
  .bar-value {
    width: 100%;
    text-align: center;
  }
  
  .chart-container {
    height: 300px;
  }
  
  .table-header,
  .table-row {
    grid-template-columns: 50px 100px 100px 100px 100px;
    gap: 8px;
    font-size: 12px;
  }
}
</style>
